import React, { useEffect, useState } from "react";
import { useContext } from "react";
import { DataGrid } from "@mui/x-data-grid";
import SendIcon from "@mui/icons-material/Send";
import api from "../../../src/utils/api";
import { Link } from "react-router-dom";
import { exportRequestsToCSV } from "./exportCsv"; 
import { PermissionsContext } from "../../../src/utils/PermissionsContext";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  TextField,
  Tabs,
  Tab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
} from "@mui/material";


const Integrations = () => {
  const [requests, setRequests] = useState([]);
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [viewMode, setViewMode] = useState("cards");
  const username = localStorage.getItem("username");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exportSummary, setExportSummary] = useState(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportedRecords, setExportedRecords] = useState([]);
  const [members, setMembers] = useState([]);
  const permissions = useContext(PermissionsContext);
  const hasPermission = (perm) => permissions?.includes(perm);
  const [hoursMap, setHoursMap] = useState({});
  const [commentsMap, setCommentsMap] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
const [activeTab, setActiveTab] = useState({});



const handleTabChange = (id, newValue) => {
  setActiveTab(prev => ({ ...prev, [id]: newValue }));
  if (newValue === "comments") {
    const workOrderId = requests.find(r => r.id === id)?.work_order_id;
    if (workOrderId) fetchComments(workOrderId);
  }
};

  useEffect(() => {
    fetchBillableRequests();
    fetchWorkOrderTypes();
    fetchMembers();  
  }, []);

  const fetchBillableRequests = async () => {
  try {
    const res = await api.get("/billable-requests");
    const normalized = res.data.map(r => ({
      ...r,
      price: r.price ?? 0,
      labor_price: r.labor_price ?? 0,
      hours: r.hours ?? 0,
    }));
    setRequests(normalized);
  } catch (err) {
    console.error("Failed to fetch billable requests:", err);
  }
};

const fetchComments = async (work_order_id) => {
  if (!work_order_id) return;
  setLoadingComments(prev => ({ ...prev, [work_order_id]: true }));
  try {
    const res = await api.get(`/getComments/${work_order_id}`);
    setCommentsMap(prev => ({ ...prev, [work_order_id]: res.data }));
  } catch (err) {
    console.error("Failed to fetch comments:", err);
  } finally {
    setLoadingComments(prev => ({ ...prev, [work_order_id]: false }));
  }
};


useEffect(() => {
  if (requests.length) {
    const initialHours = {};
    requests.forEach(r => { initialHours[r.id] = 0; });
    setHoursMap(initialHours);
  }
}, [requests]);

useEffect(() => {
  if (requests.length) {
    const defaults = {};
    requests.forEach(r => {
      defaults[r.id] = "details";
    });
    setActiveTab(defaults);
  }
}, [requests]);



  const fetchMembers = async () => {
    try {
      const res = await api.get("/members?search=");
      setMembers(res.data.members);
    } catch (err) {
      console.error("Failed to fetch members:", err);
    }
  };

  
  const fetchWorkOrderTypes = async () => {
    try {
      const res = await api.get("/workorder-types");
      setWorkOrderTypes(res.data);
    } catch (err) {
      console.error("Failed to fetch work order types:", err);
    }
  };

  const getMemberInfo = (member_number) => {
  if (!member_number) return null;
  const formatted = String(member_number).padStart(6, "0");
  return members.find(m => m.ClubMemberCode === formatted);
};
  
 const handleSendToJonas = async (requestId) => {
  const req = requests.find((r) => r.id === requestId);
  const sentBy = localStorage.getItem("username") || "unknown_user";
  const member = getMemberInfo(req.member_number); 
  const memberTypeCode = member?.ClubMembershipTypeCode || null;

  try {
    await api.post("/jonas-exports", {
      billable_request_id: requestId,
      sent_by: sentBy,
      member_type_code: memberTypeCode, 
    });

    setRequests((prev) => prev.filter((r) => r.id !== requestId));
    console.log(`Request ${requestId} sent to Jonas`);
  } catch (err) {
    console.error("Failed to send to Jonas:", err);
    alert("Failed to send request to Jonas. Check console for details.");
  }
};


  const handleHoursChange = (id, hoursStr) => {
  // Keep raw input in state so the field reflects what user typed
  const hours = hoursStr === "" ? "" : parseFloat(hoursStr) || 0;

  setHoursMap(prev => ({ ...prev, [id]: hours }));

  // If hours is a number and type is 1 or 2 → recalc labor
  if (hours !== "" && !isNaN(hours)) {
    setRequests(prev =>
      prev.map(r =>
        r.id === id && ["1", "2"].includes(String(r.work_order_type))
          ? { ...r,hours,  labor_price: parseFloat((hours * 43).toFixed(2)) }
          : r
      )
    );
  }
};


  const handlePriceChange = (id, value, field) => {
    setRequests((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, [field]: parseFloat(value) || 0 } : r
      )
    );
  };
  

  const savePrice = async (req) => {
    try {
      await api.put(`/billable-requests/${req.id}`, {
        ...req,
        price: req.price,
        labor_price: req.labor_price || 0,
      });
    } catch (err) {
      console.error("Failed to update price:", err);
    }
  };

  
const handleSendAll = async () => {
  const billableRequests = requests.filter(r => r.billable);
  const sentBy = username || "unknown_user";
  const detailedResults = [];

  for (const req of billableRequests) {
    try {
      // ✅ Find member and get their type code
      const member = getMemberInfo(req.member_number);
      const memberTypeCode = member?.ClubMembershipTypeCode || null;

      // ✅ Send to backend with type code
      await api.post("/jonas-exports", {
        billable_request_id: req.id,
        sent_by: sentBy,
        member_type_code: memberTypeCode,
      });

      // ✅ Build local export summary
      if (req.price > 0) {
        detailedResults.push({
          member_number: req.member_number,
          invoice_date: req.closed_at,
          chit_number: req.work_order_id?.toString().padStart(6, '0') || '—',
          item_group: req.work_order_type,
          export_description: `${req.description} - MATERIAL`,
          total: parseFloat(req.price).toFixed(2),
        });
      }

      if (req.labor_price > 0) {
        detailedResults.push({
          member_number: req.member_number,
          invoice_date: req.closed_at,
          chit_number: req.work_order_id?.toString().padStart(6, '0') || '—',
          item_group: req.work_order_type,
          export_description: `${req.description} - LABOR`,
          total: parseFloat(req.labor_price).toFixed(2),
        });
      }

    } catch (err) {
      console.error(`Failed to export ID ${req.id}`, err);
    }
  }

  await fetchBillableRequests();
  setExportedRecords(detailedResults);
  setExportModalOpen(true);
  setConfirmOpen(false);
};

  
  

  const handleRowUpdate = async (updatedRow) => {
    try {
      await api.put(`/billable-requests/${updatedRow.id}`, updatedRow);
      return updatedRow;
    } catch (err) {
      console.error("Error updating row:", err);
      throw err;
    }
  };

  const getRequestTypeLabel = (req) => {
    if (req.work_order_id) return `Work Order ID: ${req.work_order_id}`;
    if (req.violation_id) return `Violation ID: ${req.violation_id}`;
    if (req.architectural_request_id) return `Architectural Request ID: ${req.architectural_request_id}`;
    if (req.general_request_id) return `General Request ID: ${req.general_request_id}`;
    return "Request Type: Unknown";
  };

  const getTypeName = (req) => {
    if (req.work_order_id && req.work_order_type) {
      const match = workOrderTypes.find((w) => String(w.id) === String(req.work_order_type));
      return match ? `Work Order Type: ${match.type}` : "Work Order Type: Unknown";
    }
    if (req.violation_id && req.violation_type_name) return `Violation Type: ${req.violation_type_name}`;
    if (req.architectural_request_id && req.architectural_type_name) return `Architectural Type: ${req.architectural_type_name}`;
    return "Type: Unknown";
  };

  const columns = [
    { field: "title", headerName: "Title", width: 200 },
    { field: "description", headerName: "Description", width: 250 },
    {
      field: "type",
      headerName: "Type",
      width: 220,
      renderCell: (params) => (
        <Typography variant="body2">{getTypeName(params.row)}</Typography>
      ),
    },
    {
      field: "price",
      headerName: "Price",
      width: 130,
      editable: true,
      type: "number",
      valueFormatter: (params) =>
        params.value != null ? `$${parseFloat(params.value).toFixed(2)}` : "$0.00",
    },
    {
      field: "action",
      headerName: "Action",
      width: 180,
      renderCell: (params) =>
        params.row.billable ? (
          <Button
            variant="outlined"
            size="small"
            endIcon={<SendIcon />}
            onClick={() => handleSendToJonas(params.row.id)}
          >
            Send to Jonas
          </Button>
        ) : null,
    },
  ];


  return (
    <Box sx={{ padding: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Billable Requests
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
        {hasPermission("reload_billable_requests") && (
  <Button variant="outlined" onClick={fetchBillableRequests}>
    Reload All
  </Button>
)}
  <Button
  variant="outlined"
  onClick={() => exportRequestsToCSV(requests, getMemberInfo, getTypeName)}
>
  Export CSV
</Button>


  {exportSummary && (
  <Box sx={{ mt: 2, p: 2, backgroundColor: "#f0f4f8", borderRadius: 2 }}>
    <Typography variant="subtitle1" fontWeight="bold">
      Export Summary
    </Typography>
    <Typography>✅ {exportSummary.successCount} records exported successfully.</Typography>
    {exportSummary.failed.length > 0 && (
      <Typography color="error">
        ❌ Failed to export: {exportSummary.failed.join(", ")}
      </Typography>
    )}
  </Box>
)}
 {hasPermission("send_all_to_jonas") && (
  <Button
    variant="contained"
    color="primary"
    startIcon={<SendIcon />}
    onClick={() => setConfirmOpen(true)}
  >
    Send All to Jonas
  </Button>
)}

</Box>

      </Box>

      {viewMode === "cards" ? (
       <>
        <Grid container spacing={3}>
        {requests.map((req) => {
  const member = getMemberInfo(req.member_number); 
  const memberTypeCode = member?.ClubMembershipTypeCode || null;

  return (
    <Grid item xs={12} md={6} lg={4} key={req.id}>
      <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
    <CardContent>
  {/* Tabs Header */}
  <Tabs
    value={activeTab[req.id] || "details"}
    onChange={(e, val) => handleTabChange(req.id, val)}
    textColor="primary"
    indicatorColor="primary"
    sx={{ mb: 2 }}
  >
    <Tab label="Details" value="details" />
    {req.work_order_id && <Tab label="Comments" value="comments" />}
  </Tabs>

  {/* === DETAILS TAB === */}
  {activeTab[req.id] === "details" && (
    <>
      {/* Title */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h6" fontWeight="bold">
          {req.title}
        </Typography>
        <Typography variant="subtitle2" fontWeight="bold" align="right">
          Material: ${parseFloat(req.price || 0).toFixed(2)} <br />
          Labor: ${parseFloat(req.labor_price || 0).toFixed(2)} <br />
          <strong>
            Total: $
            {(parseFloat(req.price || 0) + parseFloat(req.labor_price || 0)).toFixed(2)}
          </strong>
        </Typography>
      </Box>

      {/* Member Info */}
      {req.member_number && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Member #: {req.member_number}
        </Typography>
      )}

      {member && (
        <>
          <Typography variant="body2">
            Name: {member.FirstName} {member.LastName}
          </Typography>
          <Typography variant="body2">
            Address: {member.FullAddress || "—"}
          </Typography>
          <Typography variant="body2">
            Membership Type: {member.ClubMembershipTypeCode || "—"}
          </Typography>
        </>
      )}

      {/* Description */}
      {req.description && req.description !== req.title && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {req.description}
        </Typography>
      )}

      {/* Billable Tag */}
      <Box sx={{ mt: 2 }}>
        <Chip
          label={req.billable ? "Billable" : "Non-Billable"}
          color={req.billable ? "success" : "default"}
        />
      </Box>

      {/* Type and Details */}
      <Typography variant="body2" sx={{ mt: 1 }}>
        {getRequestTypeLabel(req)}
      </Typography>
      <Typography variant="body2">{getTypeName(req)}</Typography>
      <Typography variant="body2">
        Started: {new Date(req.started_at).toLocaleDateString()}
      </Typography>
      <Typography variant="body2">
        Closed: {new Date(req.closed_at).toLocaleDateString()}
      </Typography>
      <Typography variant="body2">Closed By: {req.closed_by}</Typography>

      {/* Editable Price and Labor Price Fields */}
      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
        {/* Material Price */}
        <TextField
          label="Material Price"
          size="small"
          type="number"
          value={req.price ?? 0}
          onChange={(e) => handlePriceChange(req.id, e.target.value, "price")}
          sx={{ width: 110 }}
        />

        {/* Hours + Labor Price for types 1 and 2 */}
        {["1", "2"].includes(String(req.work_order_type)) ? (
          <>
            <TextField
              label="Hours"
              size="small"
              type="number"
              inputProps={{ step: "0.01" }}
              value={req.hours ?? 0}
              onChange={(e) => handleHoursChange(req.id, e.target.value)}
              sx={{ width: 90 }}
            />
            <TextField
              label="Labor Price"
              size="small"
              value={req.labor_price ?? 0}
              InputProps={{ readOnly: true }}
              sx={{ width: 110 }}
            />
          </>
        ) : (
          // Editable labor price for other types
          <TextField
            label="Labor Price"
            size="small"
            type="number"
            value={req.labor_price ?? 0}
            onChange={(e) =>
              handlePriceChange(req.id, e.target.value, "labor_price")
            }
            sx={{ width: 110 }}
          />
        )}

        {/* Save button */}
        <Button
          variant="outlined"
          onClick={() => savePrice(req)}
          disabled={!req.billable}
        >
          Save
        </Button>
      </Box>

      {/* Action Buttons */}
      {req.billable && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Left-aligned Button */}
          {hasPermission("send_single_to_jonas") && req.billable && (
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              onClick={() => handleSendToJonas(req.id)}
              sx={{ minWidth: 150 }}
            >
              Send to Jonas
            </Button>
          )}

          {/* Right-aligned Button */}
          {req.work_order_id && (
            <Button
              variant="outlined"
              component={Link}
              to={`/home/workorders/${req.work_order_id}`}
              sx={{ minWidth: 150 }}
            >
              View Work Order
            </Button>
          )}
        </Box>
      )}
    </>
  )}

  {/* === COMMENTS TAB === */}
  {activeTab[req.id] === "comments" && (
    <Box sx={{ mt: 1, maxHeight: 300, overflowY: "auto" }}>
      {loadingComments[req.work_order_id] ? (
        <CircularProgress size={24} />
      ) : commentsMap[req.work_order_id]?.length ? (
        commentsMap[req.work_order_id].map((c) => (
          <Box
            key={c.comment_id}
            sx={{
              mb: 1,
              p: 1.2,
              border: "1px solid #eee",
              borderRadius: 2,
              backgroundColor: c.is_internal ? "#fafafa" : "#fff",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {c.user_name} {c.user_lastname}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
              {c.comment_text}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(c.sent_at).toLocaleString()}
            </Typography>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No comments available.
        </Typography>
      )}
    </Box>
  )}
</CardContent>

</Card>

            </Grid>
       );       
      })}
        </Grid>

<Dialog
  open={confirmOpen}
  onClose={() => setConfirmOpen(false)}
  aria-labelledby="confirm-dialog"
  TransitionComponent={Slide}
  TransitionProps={{ direction: 'up' }}
>
  <DialogTitle id="confirm-dialog">Confirm Export</DialogTitle>
  <DialogContent>
    <Typography>
      Are you sure you want to send <strong>ALL</strong> billable requests to Jonas?
    </Typography>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
    <Button
      onClick={handleSendAll}
      variant="contained"
      color="primary"
      startIcon={<SendIcon />}
    >
      Yes, Send All
    </Button>
  </DialogActions>
</Dialog>

<Dialog
  open={exportModalOpen}
  onClose={() => setExportModalOpen(false)}
  maxWidth="md"
  fullWidth
  TransitionComponent={Slide}
  TransitionProps={{ direction: 'up' }}
>
  <DialogTitle>Export Report</DialogTitle>
  <DialogContent>
    <Typography variant="subtitle1" sx={{ mb: 2 }}>
      The following records were sent to Jonas:
    </Typography>
    <Box sx={{ maxHeight: 400, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ backgroundColor: "#f5f5f5" }}>
            <th style={{ padding: 8, textAlign: "left" }}>Member #</th>
            <th style={{ padding: 8, textAlign: "left" }}>Invoice Date</th>
            <th style={{ padding: 8, textAlign: "left" }}>Chit #</th>
            <th style={{ padding: 8, textAlign: "left" }}>Item Group</th>
            <th style={{ padding: 8, textAlign: "left" }}>Description</th>
            <th style={{ padding: 8, textAlign: "right" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {exportedRecords.map((rec, idx) => (
            <tr key={idx}>
              <td style={{ padding: 8 }}>{rec.member_number}</td>
              <td style={{ padding: 8 }}>{new Date(rec.invoice_date).toLocaleDateString()}</td>
              <td style={{ padding: 8 }}>{rec.chit_number}</td>
              <td style={{ padding: 8 }}>{rec.item_group}</td>
              <td style={{ padding: 8 }}>{rec.export_description}</td>
              <td style={{ padding: 8, textAlign: "right" }}>${rec.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setExportModalOpen(false)}>Close</Button>
  </DialogActions>
</Dialog>

</>

      ) : (

        
        <Box sx={{ height: 600, width: "100%", mt: 2 }}>
          <DataGrid
            rows={requests}
            columns={columns}
            getRowId={(row) => row.id}
            processRowUpdate={handleRowUpdate}
            experimentalFeatures={{ newEditingApi: true }}
            pageSize={10}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
          />
        </Box>

        
      )}
    </Box>
  );
};

export default Integrations;
